/**
 * Player Economy Mini Chart
 * Simple market factor chart for player economy tab
 */

export function initPlayerEconomyChart() {
  return {
    // State
    loading: false,
    error: null,
    chartReady: false,
    chart: null,
    
    // Data
    marketData: [],

    async init() {
      console.log('Initializing Player Economy Mini Chart...');
      await this.loadMarketData();
      await this.initChart();
    },

    async loadMarketData() {
      this.loading = true;
      this.error = null;
      
      try {
        // Load last 7 days of market factors
        const response = await fetch('/api/admin/metrics/series?days=7&type=economy', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`API Error: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success && data.data.series?.dailyEconomy) {
          this.marketData = data.data.series.dailyEconomy;
        }

      } catch (error) {
        console.warn('Could not load market data for mini chart:', error);
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },

    async initChart() {
      if (this.marketData.length === 0) {
        console.warn('No market data available for mini chart');
        return;
      }

      // Use requestIdleCallback to avoid blocking UI
      const initCallback = async () => {
        try {
          // Dynamic import of Chart.js
          const { Chart } = await import('chart.js/auto');
          
          await this.createMarketChart(Chart);
          
          this.chartReady = true;
          console.log('Player economy mini chart ready');
        } catch (error) {
          console.error('Error initializing mini chart:', error);
          this.error = 'Failed to initialize chart';
        }
      };

      if (window.requestIdleCallback) {
        window.requestIdleCallback(initCallback);
      } else {
        setTimeout(initCallback, 0);
      }
    },

    async createMarketChart(Chart) {
      const canvas = document.querySelector('[data-testid="player-econ-mini-chart"]');
      if (!canvas) {
        console.warn('Player mini chart canvas not found');
        return;
      }

      // Destroy existing chart
      if (this.chart) {
        this.chart.destroy();
      }

      const labels = this.marketData.map(day => {
        const date = new Date(day.dateKey);
        return date.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
      });

      const marketFactorData = this.marketData.map(day => day.marketFactor);

      this.chart = new Chart(canvas, {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: 'Factor de Mercado',
              data: marketFactorData,
              borderColor: 'rgb(59, 130, 246)',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              tension: 0.4,
              pointRadius: 3,
              pointHoverRadius: 5,
              fill: true
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              display: true,
              grid: {
                display: false
              },
              ticks: {
                font: {
                  size: 10
                }
              }
            },
            y: {
              display: true,
              beginAtZero: false,
              grid: {
                color: 'rgba(0, 0, 0, 0.1)'
              },
              ticks: {
                font: {
                  size: 10
                }
              }
            }
          },
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: 'white',
              bodyColor: 'white',
              borderColor: 'rgba(59, 130, 246, 0.5)',
              borderWidth: 1
            }
          },
          elements: {
            line: {
              borderWidth: 2
            }
          }
        }
      });
    },

    destroy() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
      this.chartReady = false;
    }
  };
}